import { Theme } from '@providers/Theme';

/* ------------------ UTILITIES ------------------ */

export type Modify<A extends object, B extends object> = Omit<A, keyof B> & B;
export type ColorProp = string | ((theme: Theme) => string | undefined);

export type PickByValue<T, ValueType> = Pick<
  T,
  { [Key in keyof T]-?: ValueType extends T[Key] ? Key : never }[keyof T]
>;

export type AppNavigation = {
  NewOrders: null;
  NewOrder: { id: string };
  AcceptedOrder: { id: string };
  OrderHistory: { id: string };
  AcceptedOrders: null;
  OrdersHistory: null;
  Trips: null;
  Menu: null;
};

export type OrderState =
  | 'PENDING'
  | 'ROUTED'
  | 'SEEN'
  | 'INSPECTING'
  | 'ACCEPTED'
  | 'FOOD_IS_READY'
  | 'REJECTED'
  | 'CANCELED'
  | 'OUT_FOR_DELIVERY'
  | 'DELIVERED';

export type LocalisedValue = {
  arName: string;
  enName: string;
};

export type I18nValue = {
  ar: string;
  en: string;
};

interface CompactObject {
  uuid: string;
  name: LocalisedValue;
}

export type FirestoreTimestamp = {
  toDate: () => Date;
};

/* ------------------ ENUMS ------------------ */
export enum DeliveryTime {
  NOW = 'NOW',
  IFTAR = 'IFTAR',
}

export enum OrderStateEnum {
  PENDING = 'PENDING',
  ROUTED = 'ROUTED',
  SEEN = 'SEEN',
  ACCEPTED = 'ACCEPTED',
  FOOD_IS_READY = 'FOOD_IS_READY',
  REJECTED = 'REJECTED',
  CANCELED = 'CANCELED',
  OUT_FOR_DELIVERY = 'OUT_FOR_DELIVERY',
  DELIVERED = 'DELIVERED',
  INSPECTING = 'INSPECTING',
}

export enum PaymentType {
  CASH_ON_DELIVERY = 'CASH_ON_DELIVERY',
  CREDIT_CARD = 'CREDIT_CARD',
  INSTAPAY_ON_DELIVERY = 'INSTAPAY_ON_DELIVERY',
}

export enum RoutingMethod {
  MANUAL = 'MANUAL',
  AUTOMATED_DESKTOP = 'AUTOMATED_DESKTOP',
  AUTOMATED_TABLET = 'AUTOMATED_TABLET',
}

export enum LastOrderActionBy {
  USER = 'USER',
  AGENT = 'AGENT',
  RESTAURANT = 'RESTAURANT',
  DRIVER = 'DRIVER',
}

export type RouteName = keyof AppNavigation;

/* ------------------ SHARED MODELS ------------------ */
export interface PromoData {
  promoUUID?: string;
  code: string;
  minOrderValue: number;
  maxMoneyValue: number;
  discount?: number | null;
  promoCodeUUID?: string;
}

export interface Location {
  lat: number;
  lon: number;
}

export interface Receipt {
  subTotal: number;
  tax: number;
  deliveryFee: number;
  total: number;
  totalBeforeDiscount: number;
  subTotalAfterDiscount?: number;
  taxOnDelivery: boolean;
  paidWithWallet: boolean;
  driverTip: number;
  walletDiscount: number;
  deliveryFeeType?: string;
}

/* ------------------ TOPPING TYPES ------------------ */

// The structure of toppings in Firestore is "Flat"
export interface ToppingFirestore {
  categoryArName: string;
  categoryEnName: string;
  categoryUUID: string;
  extraItems: {
    itemEnName: string;
    itemArName: string;
    itemUUID: string;
    orderItemExtraUUID: string;
    price: number;
    sizeArName: string;
    sizeEnName: string;
    sizeUUID: string;
  }[];
}

// The structure of toppings for the UI is "Nested"
export interface Topping {
  category: LocalisedValue & { uuid: string };
  extraItems: {
    item: LocalisedValue & { uuid: string };
    size: LocalisedValue & { uuid: string };
    orderItemExtraUUID: string;
    price: number;
  }[];
}

/* ------------------ ENTRY / ITEM TYPES ------------------ */

export interface OrderEntryBase<IsFirestore extends boolean> {
  uuid: string;
  quantity: number;
  price: number;
  size: LocalisedValue & { uuid: string };
  item: LocalisedValue & {
    uuid: string;
    comment?: string;
    topping: IsFirestore extends true ? ToppingFirestore[] : Topping[];
  };
}

/* ------------------ ORDER MESSAGE DATA ------------------ */
type Ticket = {};
type ComplaintDetails = {};

export interface OrderStateTrace {
  uuid: string;
  data: OrderStateTraceData;
}
interface OrderStateTraceData {
  acceptedBy?: string;
  acceptedTime?: string;
  cancelTime?: string;
  canceledBy?: string;
  clientAcceptedTime?: string;
  clientCancelTime?: string;
  clientOutForDeliveryTime?: string;
  clientRejectedTime?: string;
  clientRoutedTime?: string;
  clientSeenTime?: string;
  outForDeliveryTime?: string;
  pendingTime: string;
  rejectedBy?: string;
  rejectedTime?: string;
  routedTime?: string;
  seenTime?: string;
  violatingHandlingTime: boolean;
  violatingWaitingTime: boolean;
}

export interface OrderMessageDataBase<IsFirestore extends boolean> {
  clientModel: string;
  orderShortCode: string;
  version: string;
  state: OrderStateEnum;
  method: RoutingMethod;
  group: boolean;
  orderLevelSettlement: boolean;
  deliveryTime: DeliveryTime;
  nonPartner?: boolean;
  isIntegration?: boolean;
  assignedLiveTrackerAgent?: AssignedLiveTrackerAgent;
  expectedDeliveryDuration: number;
  expectedCookingDuration?: number;
  fleet: boolean;
  updateDetails?: OrderActionDetailsRequest;
  lastOrderActionBy: LastOrderActionBy;
  orderStateTrace: OrderStateTrace;
  categories: (LocalisedValue & {
    items: OrderEntryBase<IsFirestore>[];
  })[];
  receipt: Receipt;
  paymentType: PaymentType;
  user: {
    userUUID: string;
    email: string;
    name: string;
    phone: string;
  };
  deliveryAddress: {
    uuid: string;
    address: string;
    fullName: string;
    phoneNumber: string;
    location?: Location;
    buildingNumber: string;
    floor?: string;
    apartmentNumber?: string;
    area: CompactObject;
    city: CompactObject;
    zone: CompactObject;
    company: boolean;
    comanyName?: string;
  };
  tickets?: Ticket[];
  complaintDetails?: ComplaintDetails;
  userPromo?: PromoData;
}

interface OrderActionDetailsRequest {
  actionType: string;
  deliveryDuration?: number;
  comment?: string;
  cookingDuration?: number;
}

// ************************************************************************************************
// Order Compact
// ************************************************************************************************
export type OrderCompact = {
  uuid: string;
  shortCode: string;
  isFleet: boolean;
  state: OrderStateEnum;
  createdAt: string;
  name: string;
  phone: string;
  address: string;
};


/* ------------------ FINAL EXPORTS ------------------ */

export interface OrderBase<TDate, IsFirestore extends boolean> {
  uuid: string;
  orderMessage: {
    uuid: string;
    ref: any; // OrderMessageRef redacted for brevity
    data: OrderMessageDataBase<IsFirestore>;
    trace: {
      created: { at: TDate; by: string };
      updated: { at: TDate; by: string };
    };
  };
  orderStateTrace: {
    data: {
      pendingTime: string;
      violatingWaitingTime: boolean;
      violatingHandlingTime: boolean;
      seenTime?: string;
      clientSeenTime?: string;
      clientRoutedTime?: string;
      routedTime?: string;
      acceptedTime?: string;
      rejectedTime?: string;
    };
    uuid: string;
  };
  expireAt?: TDate;
  tripDriverCache?: OrderDriver;
}

export interface OrderMessageCategory {
  arName: string;
  enName: string;
  items: OrderMessageEntry[];
}
export interface OrderMessageEntry {
  uuid: string;
  quantity: number;
  price: number;
  size: OrderMessageSize;
  item: OrderMessageItem;
}

interface OrderMessageSize {
  uuid: string;
  arName: string;
  enName: string;
}

interface OrderMessageItem {
  uuid: string;
  enName: string;
  arName: string;
  comment?: string;
  topping: Topping[];
}

// ************************************************************************************************
// OrderSummary
// ************************************************************************************************
export type RefSummary = {
  branchUUID: string;
  restaurantUUID: string;
};

export type UserSummary = {
  address: string;
  name: string;
  phone: string;
};

export type AssignedLiveTrackerAgent = {
  userCode: string;
  userUUID: string;
};

export type OrderDriver = {
  uuid: string;
  name: string;
  phoneNumber: string;
  photo: string;
  customerUUID: string;
  tripStatus: string;
  location: Location;
  acceptedTime: string;
  estimatedMovingDuration: number;
  expectedRemainingTime: number;
};

export type TripDriver = {
  orderUUID: string;
  tripDriverCache?: TripDriverCache;
};
export type TripDriverCache = {
  driverUUID: string;
  driverName: string;
  driverPhoneNumber: string;
  driverPhoto: string;
  customerUUID: string;
  tripStatus: string;
  location: Location;
  acceptedTime: string;
  estimatedMovingDuration: number;
};


export type FirestoreOrderSummary = {
  uuid: string;
  shortCode: string;
  state:  OrderStateEnum;
  createdAt: FirestoreTimestamp;
  seenAt?: FirestoreTimestamp;
  refSummary: RefSummary | null;
  userSummary: UserSummary;
  assignedLiveTrackerAgent: AssignedLiveTrackerAgent | null;
  isFleet: boolean;
};

export type OrderSummary = Modify<
  FirestoreOrderSummary,
  {
    createdAt: Date;
    seenAt?: Date;
    tripDriverCache?: OrderDriver;
  }
>;

export type Order = OrderBase<Date, false>;
export type FirestoreOrder = OrderBase<FirestoreTimestamp, true>;
export type HistoryOrder = OrderBase<Date, true>;